export const DESKTOP_PREFERENCES_STORAGE_KEY = "zagreus.desktop.settings.v1";
export const DESKTOP_PREFERENCES_VERSION = 2;

export const LEGACY_LAYOUT_STORAGE_KEY = "zagreus.desktop.layout.v1";
export const LEGACY_LAST_STATION_STORAGE_KEY = "zagreus.radio.lastStationRoute";

export const DESKTOP_WALLPAPERS = ["blue", "black", "mango"] as const;
export const RADIO_EQUALIZER_SLIDERS = [
	"preamp",
	60,
	170,
	310,
	600,
	1000,
	3000,
	6000,
	12000,
	14000,
	16000,
] as const;

export type DesktopWallpaper = (typeof DESKTOP_WALLPAPERS)[number];
export type RadioEqualizerSlider = (typeof RADIO_EQUALIZER_SLIDERS)[number];

export type RadioPreferences = {
	volume: number;
	equalizer: {
		enabled: boolean;
		auto: boolean;
		sliders: Record<RadioEqualizerSlider, number>;
	};
};

export type DesktopPreferences = {
	version: typeof DESKTOP_PREFERENCES_VERSION;
	rememberWindowPositions: boolean;
	wallpaper: DesktopWallpaper;
	playerHidden: boolean;
	stationRoute: string | null;
	radio: RadioPreferences;
	windowLayout: unknown;
};

type PreferenceStorage = Pick<Storage, "getItem" | "setItem"> &
	Partial<Pick<Storage, "removeItem">>;

type LegacyPreferences = {
	stationRoute?: unknown;
	windowLayout?: unknown;
};

const DEFAULT_EQUALIZER_SLIDERS: Record<RadioEqualizerSlider, number> = {
	preamp: 50,
	60: 50,
	170: 50,
	310: 50,
	600: 50,
	1000: 50,
	3000: 50,
	6000: 50,
	12000: 50,
	14000: 50,
	16000: 50,
};

export const DEFAULT_RADIO_PREFERENCES: RadioPreferences = {
	volume: 50,
	equalizer: {
		enabled: false,
		auto: false,
		sliders: DEFAULT_EQUALIZER_SLIDERS,
	},
};

export const DEFAULT_DESKTOP_PREFERENCES: DesktopPreferences = {
	version: DESKTOP_PREFERENCES_VERSION,
	rememberWindowPositions: true,
	wallpaper: "blue",
	playerHidden: false,
	stationRoute: null,
	radio: DEFAULT_RADIO_PREFERENCES,
	windowLayout: null,
};

function cloneRadioPreferences(
	preferences: RadioPreferences = DEFAULT_RADIO_PREFERENCES,
): RadioPreferences {
	return {
		volume: preferences.volume,
		equalizer: {
			enabled: preferences.equalizer.enabled,
			auto: preferences.equalizer.auto,
			sliders: { ...preferences.equalizer.sliders },
		},
	};
}

function defaultDesktopPreferences(): DesktopPreferences {
	return {
		...DEFAULT_DESKTOP_PREFERENCES,
		radio: cloneRadioPreferences(),
	};
}

function validSliderValue(value: unknown): value is number {
	return (
		typeof value === "number" &&
		Number.isFinite(value) &&
		value >= 0 &&
		value <= 100
	);
}

function parseStoredJson(raw: string | null): unknown {
	if (raw === null) return null;
	try {
		return JSON.parse(raw);
	} catch {
		return null;
	}
}

export function isDesktopWallpaper(value: unknown): value is DesktopWallpaper {
	return DESKTOP_WALLPAPERS.some((wallpaper) => wallpaper === value);
}

export function restoreRadioPreferences(candidate: unknown): RadioPreferences {
	if (!candidate || typeof candidate !== "object") {
		return cloneRadioPreferences();
	}

	const persisted = candidate as Record<string, unknown>;
	const equalizer =
		persisted.equalizer && typeof persisted.equalizer === "object"
			? (persisted.equalizer as Record<string, unknown>)
			: {};
	const sliders =
		equalizer.sliders && typeof equalizer.sliders === "object"
			? (equalizer.sliders as Record<string, unknown>)
			: {};
	const restoredSliders = { ...DEFAULT_EQUALIZER_SLIDERS };
	for (const slider of RADIO_EQUALIZER_SLIDERS) {
		const value = sliders[String(slider)];
		if (validSliderValue(value)) restoredSliders[slider] = Math.round(value);
	}

	return {
		volume: validSliderValue(persisted.volume)
			? Math.round(persisted.volume)
			: DEFAULT_RADIO_PREFERENCES.volume,
		equalizer: {
			enabled:
				typeof equalizer.enabled === "boolean"
					? equalizer.enabled
					: DEFAULT_RADIO_PREFERENCES.equalizer.enabled,
			auto:
				typeof equalizer.auto === "boolean"
					? equalizer.auto
					: DEFAULT_RADIO_PREFERENCES.equalizer.auto,
			sliders: restoredSliders,
		},
	};
}

export function restoreDesktopPreferences(
	candidate: unknown,
	legacy: LegacyPreferences = {},
): DesktopPreferences {
	const defaults = defaultDesktopPreferences();
	if (candidate === "remember" || candidate === "forget") {
		return {
			...defaults,
			rememberWindowPositions: candidate === "remember",
			stationRoute:
				typeof legacy.stationRoute === "string" ? legacy.stationRoute : null,
			windowLayout: legacy.windowLayout ?? null,
		};
	}

	if (!candidate || typeof candidate !== "object") {
		return {
			...defaults,
			stationRoute:
				typeof legacy.stationRoute === "string" ? legacy.stationRoute : null,
			windowLayout: legacy.windowLayout ?? null,
		};
	}

	const persisted = candidate as Record<string, unknown>;
	const currentVersion = persisted.version === DESKTOP_PREFERENCES_VERSION;
	const legacyVersion = persisted.version === 1;
	if (!currentVersion && !legacyVersion) return defaults;

	return {
		version: DESKTOP_PREFERENCES_VERSION,
		rememberWindowPositions:
			typeof persisted.rememberWindowPositions === "boolean"
				? persisted.rememberWindowPositions
				: defaults.rememberWindowPositions,
		wallpaper: isDesktopWallpaper(persisted.wallpaper)
			? persisted.wallpaper
			: defaults.wallpaper,
		playerHidden:
			currentVersion && typeof persisted.playerHidden === "boolean"
				? persisted.playerHidden
				: defaults.playerHidden,
		stationRoute: currentVersion
			? typeof persisted.stationRoute === "string"
				? persisted.stationRoute
				: null
			: typeof legacy.stationRoute === "string"
				? legacy.stationRoute
				: null,
		radio: currentVersion
			? restoreRadioPreferences(persisted.radio)
			: cloneRadioPreferences(),
		windowLayout: currentVersion
			? (persisted.windowLayout ?? null)
			: (legacy.windowLayout ?? null),
	};
}

export function readDesktopPreferences(
	storage: PreferenceStorage = window.localStorage,
): DesktopPreferences {
	try {
		const raw = storage.getItem(DESKTOP_PREFERENCES_STORAGE_KEY);
		const candidate =
			raw === "remember" || raw === "forget" ? raw : parseStoredJson(raw);
		const legacyStation = storage.getItem(LEGACY_LAST_STATION_STORAGE_KEY);
		const legacyLayoutRaw = storage.getItem(LEGACY_LAYOUT_STORAGE_KEY);
		const preferences = restoreDesktopPreferences(candidate, {
			stationRoute: legacyStation,
			windowLayout: parseStoredJson(legacyLayoutRaw),
		});

		const serialized = JSON.stringify(preferences);
		if (
			(raw !== null && raw !== serialized) ||
			legacyStation !== null ||
			legacyLayoutRaw !== null
		) {
			try {
				writeDesktopPreferences(preferences, storage);
				storage.removeItem?.(LEGACY_LAST_STATION_STORAGE_KEY);
				storage.removeItem?.(LEGACY_LAYOUT_STORAGE_KEY);
			} catch {
				// Keep the legacy values available if migration cannot be written.
			}
		}

		return preferences;
	} catch {
		return defaultDesktopPreferences();
	}
}

export function writeDesktopPreferences(
	preferences: DesktopPreferences,
	storage: PreferenceStorage = window.localStorage,
): void {
	storage.setItem(DESKTOP_PREFERENCES_STORAGE_KEY, JSON.stringify(preferences));
}
